import { FilterDescriptor, State, process } from '@progress/kendo-data-query';
import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import {
  GridDataStateChangeEvent,
  GridHeaderSelectionChangeEvent,
  GridPagerSettings,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid';
import { GridSelectableSettings } from '@progress/kendo-react-grid/dist/npm/interfaces/GridSelectableSettings';
import { AxiosResponse } from 'axios';
import { Dispatch, FC, SetStateAction, useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useGLCodeCategories } from 'src/assets/static/view_department/useDepartmentCategories';
import { useGLCodeColumn } from 'src/assets/static/view_department/useGLCodeColumn';
import GridWrapper from 'src/components/GridWrapper';
import useViewDepartment from 'src/hooks/useViewDepartments';
import useWindowSize from 'src/hooks/useWindowSize';
import { GlCodeResponse } from 'src/types/GLCode';
import { BulkUpdateGLCodeRequest, Department } from 'src/types/department';
import { modifyFilters } from 'src/utils/requestInterceptor';
import { initialDepartmentState } from '../ViewDepartmentContext';
import { viewDepartmentFilters } from '../ViewDepartmentFilters';
import BulkUpdateTableToolbar from './BulkUpdateTableToolbar';

const selectable: GridSelectableSettings = {
  enabled: false,
  drag: false,
  cell: false,
};

const gridPagerSettings: GridPagerSettings = {
  buttonCount: 1,
  pageSizes: [10, 20, 50, 100, 5000],
};

const defaultProps = {
  tableTextId: 'GL Codes',
  DataItemKey: 'rowId',
  sortable: true,
  resizable: true,
  showColumnMenu: true,
  selectable: selectable,
  pageable: gridPagerSettings,
} as const;

const initialUnassignGLCodeDataState: State = {
  skip: 0,
  take: 5000,
  sort: [],
  filter: {
    logic: 'and',
    filters: [],
  },
};
interface UnassignGLCodeTableProps {
  handlePaneClose: () => void;
  selectedDepartments: Department[];
  unAssignGLCode: GlCodeResponse[];
  setUnAssignGLCode: Dispatch<SetStateAction<GlCodeResponse[]>>;
  bulkUpdateGLCode: (params: BulkUpdateGLCodeRequest) => Promise<AxiosResponse<any, any>>;
  isBulkUpdateGLCodeLoading: boolean;
}

const UnassignGLCodeTable: FC<UnassignGLCodeTableProps> = ({
  handlePaneClose,
  selectedDepartments,
  unAssignGLCode,
  setUnAssignGLCode,
  bulkUpdateGLCode,
  isBulkUpdateGLCodeLoading,
}) => {
  const { height } = useWindowSize();
  const dropDownList = useGLCodeCategories();
  const { keepSelectedRows, t, setDepartmentsDataState } = useViewDepartment();
  const unassignGLCodeColumns = useGLCodeColumn();

  const [state, setState] = useState(initialUnassignGLCodeDataState);
  const [selectionFilter, setSelectionFilter] = useState(dropDownList[1]);

  const selectedRows = useMemo(
    () => unAssignGLCode.filter((glCode) => glCode.selected),
    [unAssignGLCode]
  );

  const getUnassignGLCodeFilters = useCallback(
    (state: State) => {
      const filters: FilterDescriptor[] = modifyFilters(state.filter, undefined, false);
      return [...filters, ...(viewDepartmentFilters[selectionFilter.value]?.filters || [])];
    },
    [selectionFilter.value]
  );

  const { data: processedGLCodes, total } = useMemo(
    () =>
      process(unAssignGLCode, {
        ...state,
        filter: {
          logic: 'and',
          filters: getUnassignGLCodeFilters(state),
        },
      }) as {
        data: GlCodeResponse[];
        total: number;
      },
    [getUnassignGLCodeFilters, state, unAssignGLCode]
  );

  const onCheckbookSelectionChange = useCallback(
    (event: GridSelectionChangeEvent) => {
      setUnAssignGLCode((prev) => {
        const prevItems = [...prev].map((item) =>
          item.rowId === event?.dataItem.rowId ? { ...item, selected: !item.selected } : item
        );
        return prevItems;
      });
    },
    [setUnAssignGLCode]
  );

  const changeDataState = useCallback(
    (e: GridDataStateChangeEvent) => {
      setState(e.dataState);
      setUnAssignGLCode((prev) => prev?.map((row) => ({ ...row, selected: false })));
    },
    [setUnAssignGLCode]
  );

  const onHeaderSelectionChange = useCallback(
    (event: GridHeaderSelectionChangeEvent) => {
      const checkboxElement: any = event?.syntheticEvent?.target;
      const { checked } = checkboxElement;
      setUnAssignGLCode((prev) => prev?.map((row) => ({ ...row, selected: checked })));
    },
    [setUnAssignGLCode]
  );

  const handleClearSelection = useCallback(() => {
    setUnAssignGLCode((prev) => prev?.map((row) => ({ ...row, selected: false })));
  }, [setUnAssignGLCode]);

  const handleFilterChange = useCallback(
    (e: DropDownListChangeEvent) => {
      setUnAssignGLCode((prev) => prev?.map((row) => ({ ...row, selected: false })));
      setSelectionFilter(e.value);
    },
    [setUnAssignGLCode]
  );

  const handleUnassignGLCode = useCallback(async () => {
    const departments = selectedDepartments.map((department) => department.departmentId);
    const glCodes = processedGLCodes.filter((glCode) => glCode.selected).map((glCode) => glCode.id);

    try {
      await bulkUpdateGLCode({ departments, glCodes, assign: false });
      toast.success(t('GL Codes unassigned from departments successfully'));
      keepSelectedRows();
      handlePaneClose();
      setDepartmentsDataState(initialDepartmentState);
    } catch (error) {
      toast.error(t('Failed to unassign GL Codes from departments'));
    }
  }, [
    bulkUpdateGLCode,
    handlePaneClose,
    processedGLCodes,
    keepSelectedRows,
    selectedDepartments,
    setDepartmentsDataState,
    t,
  ]);

  const gridToolbarChildren = useMemo(
    () => (
      <BulkUpdateTableToolbar
        isAssign={false}
        isMember={false}
        selectionFilter={selectionFilter}
        dropDownList={dropDownList}
        handleClearSelection={handleClearSelection}
        selectedRows={selectedRows}
        handleFilterChange={handleFilterChange}
        handleUpdate={handleUnassignGLCode}
        selectedDepartment={selectedDepartments}
        isLoading={isBulkUpdateGLCodeLoading}
      />
    ),
    [
      selectionFilter,
      dropDownList,
      handleClearSelection,
      selectedRows,
      handleFilterChange,
      handleUnassignGLCode,
      selectedDepartments,
      isBulkUpdateGLCodeLoading,
    ]
  );

  return (
    <main className="p-[10px] bg-[#ffffff]">
      <GridWrapper
        id="unassignGLCodeTable"
        columns={unassignGLCodeColumns}
        dataSource={processedGLCodes}
        dataState={state}
        total={total}
        height={`${height - 320}px`}
        gridToolbarChildren={gridToolbarChildren}
        onDataStateChange={changeDataState}
        {...defaultProps}
        onHeaderSelectionChange={onHeaderSelectionChange}
        onSelectionChange={onCheckbookSelectionChange}
        noRecordText={t('No records to display')}
        hideScrollBar
        loading={isBulkUpdateGLCodeLoading}
      />
    </main>
  );
};

export default UnassignGLCodeTable;
